import React, { useState } from 'react';
import styled from 'styled-components';
import { useQuery } from 'react-query';
import { adminLeagueManagementAPI } from '../services/adminApi';
import SimpleMatchGenerationControl from './SimpleMatchGenerationControl';
import DivisionAccordionManagement from './DivisionAccordionManagement';
import AdminSeasonTeamEmailsPanel from './AdminSeasonTeamEmailsPanel';
import AdminTeamSlotSwapPanel from './AdminTeamSlotSwapPanel';
import { useAdminSeasonPhaseFilter } from '../hooks/useAdminSeasonPhaseFilter';
import AdminSeasonPhaseFilters from './AdminSeasonPhaseFilters';

const Container = styled.div`
  padding: 2rem;
  max-width: 1400px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const Header = styled.div`
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    margin-bottom: 1.5rem;
  }
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 0.5rem;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const Subtitle = styled.p`
  color: #6b7280;
  font-size: 1.1rem;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const Tabs = styled.div`
  display: flex;
  border-bottom: 2px solid #e5e7eb;
  margin-bottom: 2rem;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  
  @media (max-width: 768px) {
    margin-bottom: 1.5rem;
  }
  
  &::-webkit-scrollbar {
    height: 4px;
  }
`;

const Tab = styled.button`
  padding: 1rem 2rem;
  border: none;
  background: none;
  font-size: 1rem;
  font-weight: 600;
  color: ${props => props.$active ? '#043663' : '#6b7280'};
  border-bottom: 2px solid ${props => props.$active ? '#043663' : 'transparent'};
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  flex-shrink: 0;

  &:hover {
    color: #043663;
  }
  
  @media (max-width: 768px) {
    padding: 0.75rem 1.5rem;
    font-size: 0.9rem;
  }
`;

const Content = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

// Summary Cards
const SummaryCards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  padding: 1.5rem;
  background: #f8fafc;
  border-bottom: 1px solid #e5e7eb;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 0.75rem;
    padding: 1rem;
  }
`;

const SummaryCard = styled.div`
  background: white;
  padding: 1rem;
  border-radius: 6px;
  border-left: 4px solid ${props => props.color || '#3b82f6'};
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  
  @media (max-width: 768px) {
    padding: 0.75rem;
  }
`;

const SummaryTitle = styled.h3`
  font-size: 0.875rem;
  font-weight: 600;
  color: #6b7280;
  margin: 0 0 0.5rem 0;
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
    margin-bottom: 0.25rem;
  }
`;

const SummaryValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: #1f2937;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

// Filters and Search
const FiltersSection = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const FiltersRow = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;
  }
`;

const SearchInput = styled.input`
  flex: 1;
  min-width: 300px;
  padding: 0.75rem 1rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.875rem;
  
  &:focus {
    outline: none;
    border-color: #043663;
    box-shadow: 0 0 0 3px rgba(4, 54, 99, 0.1);
  }
  
  @media (max-width: 768px) {
    min-width: 100%;
    width: 100%;
  }
`;

const FilterSelect = styled.select`
  padding: 0.75rem 1rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.875rem;
  background: white;
  min-width: 150px;
  
  &:focus {
    outline: none;
    border-color: #043663;
  }
  
  @media (max-width: 768px) {
    width: 100%;
    min-width: 100%;
  }
`;

// eslint-disable-next-line no-unused-vars
const FilterButton = styled.button`
  padding: 0.75rem 1.5rem;
  background: ${props => props.$active ? '#043663' : '#f3f4f6'};
  color: ${props => props.$active ? 'white' : '#374151'};
  border: none;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: ${props => props.active ? '#032a4f' : '#e5e7eb'};
  }
  
  @media (max-width: 768px) {
    width: 100%;
    padding: 0.85rem 1rem;
  }
`;

// Divisions Table
const TableContainer = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  
  @media (max-width: 768px) {
    margin: 0 -1rem;
    padding: 0 1rem;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 600px;
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
  }
`;

const TableHeader = styled.thead`
  background: #f8fafc;
`;

const TableHeaderCell = styled.th`
  padding: 1rem;
  text-align: left;
  font-weight: 600;
  color: #374151;
  font-size: 0.875rem;
  border-bottom: 1px solid #e5e7eb;
  white-space: nowrap;
  
  @media (max-width: 768px) {
    padding: 0.75rem 0.5rem;
    font-size: 0.75rem;
  }
`;

const TableRow = styled.tr`
  border-bottom: 1px solid #f3f4f6;
  
  &:hover {
    background: #f9fafb;
  }
`;

const TableCell = styled.td`
  padding: 1rem;
  font-size: 0.875rem;
  color: #374151;
  
  @media (max-width: 768px) {
    padding: 0.75rem 0.5rem;
    font-size: 0.8rem;
  }
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 500;
  background: ${props => {
    switch (props.$status) {
      case 'balanced': return '#d1fae5';
      case 'overflow': return '#fef3c7';
      default: return '#fee2e2';
    }
  }};
  color: ${props => {
    switch (props.$status) {
      case 'balanced': return '#065f46';
      case 'overflow': return '#92400e';
      default: return '#991b1b';
    }
  }};
`;

const TeamCount = styled.span`
  font-weight: 600;
  color: ${props => {
    if (props.$count === props.$max) return '#059669';
    if (props.$count > props.$max) return '#d97706';
    return '#dc2626';
  }};
`;

const ActionButton = styled.button`
  padding: 0.5rem 1rem;
  background: #043663;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: #032a4f;
  }
  
  &:disabled {
    background: #9ca3af;
    cursor: not-allowed;
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 3rem;
  font-size: 1.1rem;
  color: #6b7280;
`;

const ErrorMessage = styled.div`
  padding: 1rem;
  background: #fee2e2;
  color: #991b1b;
  border-radius: 6px;
  margin: 1rem;
`;

const AdminLeagueManagementV2 = ({
  /** Jump to Teams in the sidebar (same dashboard SPA). */
  onGoToTeams,
  /** Country-tenant federation player list — optional */
  onGoToPlayers,
} = {}) => {
  const [activeTab, setActiveTab] = useState('divisions');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [cantonFilter, setCantonFilter] = useState('all');
  const [levelFilter, setLevelFilter] = useState('all');

  const {
    campaignId,
    seasonId: selectedSeason,
    onCampaignChange,
    onPhaseChange,
    seasonOptions,
    phaseOptions,
    allPhases,
    isLoading: seasonsLoading,
  } = useAdminSeasonPhaseFilter({ allowAll: false, autoDefault: true });

  const hasNoSeasons = !seasonsLoading && allPhases.length === 0;

  // Fetch divisions overview
  const { data: divisionsData, isLoading, error } = useQuery(
    ['divisions-overview', selectedSeason],
    () => adminLeagueManagementAPI.getDivisionsOverview(selectedSeason),
    {
      enabled: !!selectedSeason,
      retry: false,
      onError: (error) => {
        console.error('Error fetching divisions overview:', error);
      }
    }
  );

  // Calculate summary statistics
  const getSummaryStats = () => {
    if (!divisionsData?.data?.divisions) return { 
      total: 0, optimal4: 0, optimal6: 0, full8: 0, full10: 0, full12: 0, full14: 0, needAdjustment: 0, empty: 0, totalTeams: 0 
    };
    
    const divisions = divisionsData.data.divisions;
    const unassignedTeams = divisionsData.data.unassignedTeams || [];
    const total = divisions.length;
    let optimal4 = 0, optimal6 = 0, full8 = 0, full10 = 0, full12 = 0, full14 = 0, needAdjustment = 0, empty = 0, totalTeams = 0;
    
    // Count teams in divisions
    divisions.forEach(div => {
      const teamCount = parseInt(div.actual_team_count) || 0;
      totalTeams += teamCount;
      
      if (teamCount === 0) {
        empty++;
      } else if (teamCount === 4) {
        optimal4++;
      } else if (teamCount === 6) {
        optimal6++;
      } else if (teamCount === 8) {
        full8++;
      } else if (teamCount === 10) {
        full10++;
      } else if (teamCount === 12) {
        full12++;
      } else if (teamCount === 14) {
        full14++;
      } else {
        needAdjustment++;
      }
    });
    
    // Add unassigned teams to total count
    totalTeams += unassignedTeams.length;
    
    return { total, optimal4, optimal6, full8, full10, full12, full14, needAdjustment, empty, totalTeams };
  };

  // Filter divisions based on search and filters
  const getFilteredDivisions = () => {
    if (!divisionsData?.data?.divisions) return [];
    
    // First filter out old canton codes - check both division_canton and original_canton
    const oldCantonCodes = ['BL', 'BS', 'AR', 'AI', 'NW', 'OW', 'UR', 'GR'];
    const validDivisions = divisionsData.data.divisions.filter(division => {
      const canton = division.division_canton || division.canton || '';
      const originalCanton = division.original_canton || '';
      
      // Exclude if either the mapped canton or original canton is in the old codes list
      return !oldCantonCodes.includes(canton) && !oldCantonCodes.includes(originalCanton);
    });
    
    return validDivisions.filter(division => {
      const teamCount = parseInt(division.actual_team_count) || 0;
      const status = getDivisionStatus(teamCount, division.max_teams);
      const canton = division.division_canton || division.canton || '';
      
      const matchesSearch = canton.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (division.division_level || '').toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStatus = statusFilter === 'all' || status === statusFilter;
      const matchesCanton = cantonFilter === 'all' || canton === cantonFilter;
      const matchesLevel = levelFilter === 'all' || division.division_level === levelFilter;
      
      return matchesSearch && matchesStatus && matchesCanton && matchesLevel;
    });
  };

  const getDivisionStatus = (teamCount, maxTeams) => {
    // Supported generate sizes: 4, 6, 8, 10, 12, 14
    if (teamCount === 0) return 'empty';
    if ([4, 6, 8, 10, 12, 14].includes(teamCount)) return 'optimal';
    if (teamCount > 14) return 'overflow';
    return 'needAdjustment';
  };

  // eslint-disable-next-line no-unused-vars
  const getStatusColor = (status) => {
    switch (status) {
      case 'balanced': return '#10b981';
      case 'full': return '#3b82f6';
      case 'overflow': return '#f59e0b';
      case 'empty': return '#9ca3af';
      default: return '#ef4444';
    }
  };

  // Helper function to check if season is completed (date-based check)
  const isSeasonCompleted = (season) => {
    if (!season || !season.end_date) return false;
    const now = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
    const endDate = String(season.end_date).split('T')[0]; // Extract date part
    return now > endDate;
  };

  // Get selected season data and check if it's completed
  const selectedSeasonData = selectedSeason
    ? allPhases.find((s) => String(s.id) === String(selectedSeason))
    : null;
  const isReadOnly = selectedSeasonData ? isSeasonCompleted(selectedSeasonData) : false;

  const summaryStats = getSummaryStats();
  const filteredDivisions = getFilteredDivisions();
  // Filter out old canton codes and only show new consolidated cantons
  const oldCantonCodes = ['BL', 'BS', 'AR', 'AI', 'NW', 'OW', 'UR', 'GR'];
  const uniqueCantons = [...new Set(divisionsData?.data?.divisions?.map(d => {
    const canton = d.division_canton || d.canton || '';
    const originalCanton = d.original_canton || '';
    
    // Only include if neither the mapped canton nor original canton is in the old codes list
    if (oldCantonCodes.includes(canton) || oldCantonCodes.includes(originalCanton)) {
      return null; // This will be filtered out
    }
    return canton;
  }).filter(Boolean) || [])].sort();
  
  // Define the 7 standard division levels
  const standardLevels = [
    'beginner',
    'beginner_intermediate', 
    'intermediate',
    'confirmed_intermediate',
    'advanced',
    'confirmed_advanced',
    'elite'
  ];

  if (seasonsLoading) {
    return (
      <Container>
        <LoadingSpinner>Loading phases…</LoadingSpinner>
      </Container>
    );
  }

  if (hasNoSeasons) {
    return (
      <Container>
        <Header>
          <Title>League Management</Title>
          <Subtitle>Manage team divisions and match scheduling</Subtitle>
        </Header>
        <Content style={{ padding: '2rem', textAlign: 'center' }}>
          <div style={{ fontSize: '1.125rem', color: '#6b7280', marginBottom: '0.5rem' }}>
            No phase has been created yet.
          </div>
          <div style={{ fontSize: '0.9375rem', color: '#9ca3af' }}>
            Create a phase under <strong>Settings → Tournaments</strong>, then return here to manage divisions and matches.
          </div>
        </Content>
      </Container>
    );
  }

  if (isLoading) {
    return (
      <Container>
        <LoadingSpinner>Loading divisions...</LoadingSpinner>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <ErrorMessage>Error loading divisions. Please try again.</ErrorMessage>
      </Container>
    );
  }

  if (!selectedSeason) {
    return (
      <Container>
        <LoadingSpinner>Loading phases…</LoadingSpinner>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <Title>League Management</Title>
            <Subtitle>Manage team divisions and match scheduling</Subtitle>
          </div>
          <AdminSeasonPhaseFilters
            campaignId={campaignId}
            seasonId={selectedSeason || ''}
            seasonOptions={seasonOptions}
            phaseOptions={phaseOptions}
            onCampaignChange={onCampaignChange}
            onPhaseChange={onPhaseChange}
            allowAll={false}
            disabled={seasonsLoading}
          />
        </div>
      </Header>

      <Tabs>
        <Tab 
          $active={activeTab === 'divisions'} 
          onClick={() => setActiveTab('divisions')}
        >
          Division Overview
        </Tab>
        <Tab 
          $active={activeTab === 'balancing'} 
          onClick={() => setActiveTab('balancing')}
        >
          Division Balancing
        </Tab>
        <Tab 
          $active={activeTab === 'matches'} 
          onClick={() => setActiveTab('matches')}
        >
          Match Management
        </Tab>
        <Tab
          $active={activeTab === 'team-emails'}
          onClick={() => setActiveTab('team-emails')}
        >
          Team emails
        </Tab>
        <Tab
          $active={activeTab === 'slot-swap'}
          onClick={() => setActiveTab('slot-swap')}
        >
          Slot swap
        </Tab>
      </Tabs>

      <Content>
        {activeTab === 'divisions' && (
          <>
            {/* Summary Cards */}
            <SummaryCards>
              <SummaryCard color="#1f2937">
                <SummaryTitle>Total Teams (All)</SummaryTitle>
                <SummaryValue>{divisionsData?.data?.stats?.totalTeams || 0}</SummaryValue>
                <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                  All registered teams
                </div>
              </SummaryCard>
              <SummaryCard color="#10b981">
                <SummaryTitle>Active Teams</SummaryTitle>
                <SummaryValue>{divisionsData?.data?.stats?.activeTeams || 0}</SummaryValue>
                <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                  Eligible for matches
                </div>
              </SummaryCard>
              <SummaryCard color="#dc2626">
                <SummaryTitle>Disabled Teams</SummaryTitle>
                <SummaryValue>{divisionsData?.data?.stats?.disabledTeams || 0}</SummaryValue>
                <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                  Excluded from matches
                </div>
              </SummaryCard>
              <SummaryCard color="#059669">
                <SummaryTitle>Assigned to Divisions</SummaryTitle>
                <SummaryValue>{divisionsData?.data?.stats?.assignedTeams || 0}</SummaryValue>
                <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                  Ready for match generation
                </div>
              </SummaryCard>
              <SummaryCard color="#f59e0b">
                <SummaryTitle>⚠️ Unassigned Teams</SummaryTitle>
                <SummaryValue>{divisionsData?.data?.stats?.unassignedTeams || 0}</SummaryValue>
                <div style={{ fontSize: '0.75rem', color: '#dc2626', marginTop: '0.25rem', fontWeight: '600' }}>
                  Will NOT be included in matches
                </div>
              </SummaryCard>
              <SummaryCard color="#3b82f6">
                <SummaryTitle>Total Divisions</SummaryTitle>
                <SummaryValue>{summaryStats.total}</SummaryValue>
              </SummaryCard>
              <SummaryCard color="#10b981">
                <SummaryTitle>Optimal (4 teams)</SummaryTitle>
                <SummaryValue>{summaryStats.optimal4}</SummaryValue>
              </SummaryCard>
              <SummaryCard color="#10b981">
                <SummaryTitle>Optimal (6 teams)</SummaryTitle>
                <SummaryValue>{summaryStats.optimal6}</SummaryValue>
              </SummaryCard>
              <SummaryCard color="#3b82f6">
                <SummaryTitle>Full (8 teams)</SummaryTitle>
                <SummaryValue>{summaryStats.full8}</SummaryValue>
              </SummaryCard>
              <SummaryCard color="#3b82f6">
                <SummaryTitle>Full (10 teams)</SummaryTitle>
                <SummaryValue>{summaryStats.full10}</SummaryValue>
              </SummaryCard>
              <SummaryCard color="#3b82f6">
                <SummaryTitle>Full (12 teams)</SummaryTitle>
                <SummaryValue>{summaryStats.full12 || 0}</SummaryValue>
              </SummaryCard>
              <SummaryCard color="#3b82f6">
                <SummaryTitle>Full (14 teams)</SummaryTitle>
                <SummaryValue>{summaryStats.full14 || 0}</SummaryValue>
              </SummaryCard>
              <SummaryCard color="#f59e0b">
                <SummaryTitle>Need Adjustment</SummaryTitle>
                <SummaryValue>{summaryStats.needAdjustment}</SummaryValue>
              </SummaryCard>
              <SummaryCard color="#9ca3af">
                <SummaryTitle>Empty</SummaryTitle>
                <SummaryValue>{summaryStats.empty}</SummaryValue>
              </SummaryCard>
            </SummaryCards>

            {/* Filters */}
            <FiltersSection>
              <FiltersRow>
                <SearchInput
                  type="text"
                  placeholder="Search divisions by canton or level..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                
                <FilterSelect
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="all">All Status</option>
                  <option value="optimal">Supported (4–14 even)</option>
                  <option value="needAdjustment">Need Adjustment</option>
                  <option value="empty">Empty</option>
                  <option value="overflow">Overflow (15+)</option>
                </FilterSelect>
                
                <FilterSelect
                  value={cantonFilter}
                  onChange={(e) => setCantonFilter(e.target.value)}
                >
                  <option value="all">All Cantons</option>
                  {uniqueCantons.map(canton => (
                    <option key={canton} value={canton}>{canton}</option>
                  ))}
                </FilterSelect>
                
                <FilterSelect
                  value={levelFilter}
                  onChange={(e) => setLevelFilter(e.target.value)}
                >
                  <option value="all">All Levels</option>
                  {standardLevels.map(level => (
                    <option key={level} value={level}>{level.replace('_', ' ').toUpperCase()}</option>
                  ))}
                </FilterSelect>
              </FiltersRow>
            </FiltersSection>

            {/* Divisions Table */}
            <TableContainer>
              <Table>
                <TableHeader>
                  <tr>
                    <TableHeaderCell>Division</TableHeaderCell>
                    <TableHeaderCell>Teams</TableHeaderCell>
                    <TableHeaderCell>Status</TableHeaderCell>
                    <TableHeaderCell>Skill Range</TableHeaderCell>
                    <TableHeaderCell>Actions</TableHeaderCell>
                  </tr>
                </TableHeader>
                <tbody>
                  {filteredDivisions.map((division) => {
                    const teamCount = parseInt(division.actual_team_count) || 0;
                    const status = teamCount === division.max_teams ? 'balanced' : 
                                  teamCount > division.max_teams ? 'overflow' : 'underfilled';
                    
                    return (
                      <TableRow key={division.id}>
                        <TableCell>
                          <div style={{ fontWeight: '600' }}>
                            {division.name || `${division.division_canton || division.canton} ${division.division_level}`}
                            {division.group_name && ` (${division.group_name})`}
                          </div>
                        </TableCell>
                        <TableCell>
                          <TeamCount $count={teamCount} $max={division.max_teams}>
                            {teamCount}/{division.max_teams}
                          </TeamCount>
                        </TableCell>
                        <TableCell>
                          <StatusBadge $status={status}>
                            {teamCount === 4 ? '✅ Optimal (4)' :
                             teamCount === 6 ? '✅ Optimal (6)' :
                             teamCount === 8 ? '✅ Full (8)' :
                             teamCount === 10 ? '✅ Full (10)' :
                             teamCount === 12 ? '✅ Full (12)' :
                             teamCount === 14 ? '✅ Full (14)' :
                             teamCount === 0 ? 'Empty' :
                             teamCount > 14 ? `Overflow (${teamCount})` :
                             `⚠️ Adjust (${teamCount})`}
                          </StatusBadge>
                        </TableCell>
                        <TableCell>
                          {division.skill_level_min} - {division.skill_level_max}
                        </TableCell>
                        <TableCell>
                          <ActionButton>
                            Move Teams
                          </ActionButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </tbody>
              </Table>
            </TableContainer>
          </>
        )}

        {activeTab === 'balancing' && (
          <>
            {/* Stats for Division Balancing */}
            {divisionsData?.data?.stats && (
              <SummaryCards style={{ marginBottom: '1.5rem' }}>
                <SummaryCard color="#1f2937">
                  <SummaryTitle>Total Teams (All)</SummaryTitle>
                  <SummaryValue>{divisionsData.data.stats.totalTeams}</SummaryValue>
                  <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                    All registered teams
                  </div>
                </SummaryCard>
                <SummaryCard color="#10b981">
                  <SummaryTitle>Active Teams</SummaryTitle>
                  <SummaryValue>{divisionsData.data.stats.activeTeams}</SummaryValue>
                  <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                    Eligible for matches
                  </div>
                </SummaryCard>
                <SummaryCard color="#dc2626">
                  <SummaryTitle>Disabled Teams</SummaryTitle>
                  <SummaryValue>{divisionsData.data.stats.disabledTeams}</SummaryValue>
                  <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                    Excluded from matches
                  </div>
                </SummaryCard>
                <SummaryCard color="#059669">
                  <SummaryTitle>Assigned to Divisions</SummaryTitle>
                  <SummaryValue>{divisionsData.data.stats.assignedTeams}</SummaryValue>
                  <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                    Teams shown below
                  </div>
                </SummaryCard>
                <SummaryCard color="#f59e0b">
                  <SummaryTitle>⚠️ Unassigned Teams</SummaryTitle>
                  <SummaryValue>{divisionsData.data.stats.unassignedTeams}</SummaryValue>
                  <div style={{ fontSize: '0.75rem', color: '#dc2626', marginTop: '0.25rem', fontWeight: '600' }}>
                    Will NOT be included in matches
                  </div>
                </SummaryCard>
                <SummaryCard color="#8b5cf6">
                  <SummaryTitle>Teams in Accordion</SummaryTitle>
                  <SummaryValue>
                    {divisionsData?.data?.divisions?.reduce((sum, div) => {
                      return sum + (parseInt(div.actual_team_count) || 0);
                    }, 0) || 0}
                  </SummaryValue>
                  <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                    Count manually to verify
                  </div>
                </SummaryCard>
              </SummaryCards>
            )}
            <DivisionAccordionManagement selectedSeason={selectedSeason} />
          </>
        )}

        {activeTab === 'matches' && (
          <>
            {isReadOnly && (
              <div style={{
                background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                border: '2px solid #f59e0b',
                borderRadius: '12px',
                padding: '1.5rem',
                marginBottom: '1.5rem',
                boxShadow: '0 4px 12px rgba(245, 158, 11, 0.2)'
              }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                  <div style={{ fontSize: '2rem', flexShrink: 0 }}>🏁</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '1.25rem', fontWeight: '700', color: '#92400e', marginBottom: '0.5rem' }}>
                      Viewing Completed Phase
                    </div>
                    <div style={{ color: '#78350f', fontSize: '0.95rem' }}>
                      This phase has ended. You can view match information and statistics, but match generation and other modifications are disabled.
                    </div>
                  </div>
                </div>
              </div>
            )}
            <SimpleMatchGenerationControl selectedSeason={selectedSeason} readOnly={isReadOnly} />
          </>
        )}

        {activeTab === 'team-emails' && (
          <AdminSeasonTeamEmailsPanel seasonId={selectedSeason} readOnly={isReadOnly} />
        )}

        {activeTab === 'slot-swap' && (
          <AdminTeamSlotSwapPanel
            seasonId={selectedSeason}
            readOnly={isReadOnly}
            onGoToTeams={onGoToTeams}
            onGoToPlayers={onGoToPlayers}
          />
        )}
      </Content>
    </Container>
  );
};

export default AdminLeagueManagementV2;